/**
 * Admin API functions for restaurant configuration, stats, subscriptions, orders, etc.
 */

import type {
  RestaurantAdminConfig,
  UpdateRestaurantConfigInput,
  AdminStatsResponse,
  StatsPeriod,
  RestaurantAdmin,
  AdminRole,
  AdminOrdersResponse,
  OrderStatus,
  QRTemplateId,
  QRTemplateInfo,
  ImageUploadResponse,
} from '@chatealo/shared';
import { apiFetch, getApiUrl, isNetworkError, getStatusMessage } from './api-base';
import { getInitData } from '../lib/telegram';

/**
 * Get restaurant admin configuration
 */
export async function getAdminConfig(restaurantId: string): Promise<RestaurantAdminConfig> {
  return apiFetch<RestaurantAdminConfig>(`/api/admin/${restaurantId}/config`);
}

/**
 * Update restaurant admin configuration
 */
export async function updateAdminConfig(
  restaurantId: string,
  config: UpdateRestaurantConfigInput
): Promise<RestaurantAdminConfig> {
  return apiFetch<RestaurantAdminConfig>(`/api/admin/${restaurantId}/config`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

// ============================================
// Statistics API
// ============================================

/**
 * Get admin statistics
 * @param restaurantId - The restaurant ID
 * @param period - The period to get stats for (today, week, month)
 * @param selectedDate - Optional specific date to filter stats (YYYY-MM-DD)
 */
export async function getAdminStats(
  restaurantId: string,
  period: StatsPeriod = 'today',
  selectedDate?: string | null
): Promise<AdminStatsResponse> {
  const params = new URLSearchParams({ period });
  if (selectedDate) {
    params.append('date', selectedDate);
  }
  return apiFetch<AdminStatsResponse>(`/api/admin/${restaurantId}/stats?${params.toString()}`);
}

// ============================================
// QR Code API
// ============================================

export type { QRTemplateId, QRTemplateInfo };

export interface QRCodeResponse {
  link: string;
  qrCodeDataUrl: string;
  template: QRTemplateId;
}

export interface QRCodeSendResponse {
  success: boolean;
  message: string;
}

export interface QRTemplatesResponse {
  templates: QRTemplateInfo[];
}

/**
 * Get QR code for the restaurant
 */
export async function getQRCode(
  restaurantId: string,
  template: QRTemplateId = 'basic',
  tableNumber?: number,
): Promise<QRCodeResponse> {
  const params = new URLSearchParams({ template });
  if (tableNumber !== undefined) {
    params.set('tableNumber', String(tableNumber));
  }
  return apiFetch<QRCodeResponse>(`/api/admin/${restaurantId}/qrcode?${params.toString()}`);
}

/**
 * Get available QR code templates
 */
export async function getQRTemplates(restaurantId: string): Promise<QRTemplatesResponse> {
  return apiFetch<QRTemplatesResponse>(`/api/admin/${restaurantId}/qrcode/templates`);
}

/**
 * Send QR code to the owner's Telegram chat
 */
export async function sendQRCodeToChat(
  restaurantId: string,
  template: QRTemplateId = 'basic',
  tableNumber?: number,
): Promise<QRCodeSendResponse> {
  return apiFetch<QRCodeSendResponse>(`/api/admin/${restaurantId}/qrcode/send`, {
    method: 'POST',
    body: JSON.stringify({ template, tableNumber }),
  });
}

// ============================================
// Subscription API
// ============================================

export interface SubscriptionResponse {
  status: string | null;
  currentPeriodEnd: string | null;
  portalUrl: string | null;
  trialEndsAt: string | null;
  trialStartedAt: string | null;
  suspendedAt: string | null;
  suspensionReason: string | null;
  canOperate: boolean;
}

/**
 * Get subscription info for the restaurant
 */
export async function getSubscription(restaurantId: string): Promise<SubscriptionResponse> {
  return apiFetch<SubscriptionResponse>(`/api/admin/${restaurantId}/subscription`);
}

export interface CheckoutResponse {
  checkoutUrl: string;
}

/**
 * Create a Stripe Checkout Session for subscription
 */
export async function createCheckoutSession(restaurantId: string): Promise<CheckoutResponse> {
  return apiFetch<CheckoutResponse>(`/api/admin/${restaurantId}/subscription/checkout`, {
    method: 'POST',
  });
}

// ============================================
// Restaurant Admins API
// ============================================

/**
 * Get list of restaurant administrators
 */
export async function getRestaurantAdmins(restaurantId: string): Promise<RestaurantAdmin[]> {
  const response = await apiFetch<{ admins: RestaurantAdmin[] }>(`/api/admin/${restaurantId}/admins`);
  return response.admins;
}

/**
 * Invite a new admin to the restaurant
 */
export async function inviteAdmin(
  restaurantId: string,
  telegramId: string,
  role: AdminRole
): Promise<RestaurantAdmin> {
  return apiFetch<RestaurantAdmin>(`/api/admin/${restaurantId}/admins`, {
    method: 'POST',
    body: JSON.stringify({ telegramId, role }),
  });
}

/**
 * Remove an admin from the restaurant
 */
export async function removeAdmin(
  restaurantId: string,
  adminId: string
): Promise<void> {
  await apiFetch(`/api/admin/${restaurantId}/admins/${adminId}`, {
    method: 'DELETE',
  });
}

/**
 * Update admin role
 */
export async function updateAdminRole(
  restaurantId: string,
  adminId: string,
  role: AdminRole
): Promise<RestaurantAdmin> {
  return apiFetch<RestaurantAdmin>(`/api/admin/${restaurantId}/admins/${adminId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

// ============================================
// Admin Invitations API
// ============================================

export interface AdminInvitation {
  id: string;
  restaurantId: string;
  role: 'admin' | 'viewer' | 'driver';
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
  usedBy: string | null;
}

export interface CreateInvitationResponse {
  inviteCode: string;
  expiresAt: string;
  role: 'admin' | 'viewer' | 'driver';
}

/**
 * Create an admin invitation link
 */
export async function createAdminInvitation(
  restaurantId: string,
  role: 'admin' | 'viewer' | 'driver'
): Promise<CreateInvitationResponse> {
  return apiFetch<CreateInvitationResponse>(`/api/admin/${restaurantId}/invitations`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });
}

/**
 * Get list of pending admin invitations
 */
export async function getAdminInvitations(restaurantId: string): Promise<AdminInvitation[]> {
  const response = await apiFetch<{ invitations: AdminInvitation[] }>(`/api/admin/${restaurantId}/invitations`);
  return response.invitations;
}

// ============================================
// Stripe Connect API
// ============================================

export interface ConnectStatusResponse {
  status: string | null;
  chargesEnabled: boolean;
  hasAccount: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  requirements?: string[];
}

/**
 * Get Stripe Connect status for the restaurant
 */
export async function getConnectStatus(restaurantId: string): Promise<ConnectStatusResponse> {
  const response = await apiFetch<{ data: ConnectStatusResponse }>(`/api/admin/${restaurantId}/stripe-connect`);
  return response.data;
}

export interface ConnectSetupResponse {
  url: string;
  accountId: string;
}

/**
 * Start or resume Stripe Connect onboarding
 */
export async function setupConnect(restaurantId: string): Promise<ConnectSetupResponse> {
  const response = await apiFetch<{ data: ConnectSetupResponse }>(`/api/admin/${restaurantId}/stripe-connect/setup`, {
    method: 'POST',
  });
  return response.data;
}

export interface DashboardLinkResponse {
  url: string;
}

/**
 * Get Stripe Express Dashboard login link
 */
export async function getDashboardLink(restaurantId: string): Promise<DashboardLinkResponse> {
  const response = await apiFetch<{ data: DashboardLinkResponse }>(`/api/admin/${restaurantId}/stripe-connect/dashboard`, {
    method: 'POST',
  });
  return response.data;
}

// ============================================
// Orders API
// ============================================

/**
 * Get admin orders with optional filters and pagination
 */
export async function getAdminOrders(
  restaurantId: string,
  params?: { status?: string; limit?: number; offset?: number; date?: string; scheduled?: boolean }
): Promise<AdminOrdersResponse> {
  const searchParams = new URLSearchParams();
  if (params?.scheduled) {
    searchParams.append('scheduled', 'true');
  } else {
    if (params?.status) searchParams.append('status', params.status);
    if (params?.date) searchParams.append('date', params.date);
  }
  if (params?.limit) searchParams.append('limit', String(params.limit));
  if (params?.offset) searchParams.append('offset', String(params.offset));
  const qs = searchParams.toString();
  return apiFetch<AdminOrdersResponse>(`/api/admin/${restaurantId}/orders${qs ? `?${qs}` : ''}`);
}

/**
 * Update order status from admin panel
 */
export async function updateOrderStatus(
  restaurantId: string,
  orderId: string,
  status: OrderStatus
): Promise<{ data: { id: string; status: string } }> {
  return apiFetch<{ data: { id: string; status: string } }>(`/api/admin/${restaurantId}/orders/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

/**
 * Confirm a scheduled order (without changing status)
 */
export async function confirmScheduledOrder(
  restaurantId: string,
  orderId: string
): Promise<{ data: { id: string; status: string; scheduledConfirmedAt: string } }> {
  return apiFetch<{ data: { id: string; status: string; scheduledConfirmedAt: string } }>(`/api/admin/${restaurantId}/orders/${orderId}/confirm-scheduled`, {
    method: 'PUT',
  });
}

/**
 * Sync bot profile photo from Telegram to R2
 */
export async function syncLogo(restaurantId: string): Promise<{ logoUrl: string }> {
  return apiFetch<{ logoUrl: string }>(`/api/admin/${restaurantId}/config/sync-logo`, {
    method: 'POST',
  });
}

/**
 * Sync bot display name from Telegram
 */
export async function syncName(restaurantId: string): Promise<{ name: string }> {
  return apiFetch<{ name: string }>(`/api/admin/${restaurantId}/config/sync-name`, {
    method: 'POST',
  });
}

/**
 * Upload cover image for the restaurant
 */
export async function uploadCoverImage(
  restaurantId: string,
  file: File
): Promise<ImageUploadResponse> {
  const initData = getInitData();
  const formData = new FormData();
  formData.append('image', file);

  let response: Response;
  try {
    response = await fetch(`${getApiUrl()}/api/admin/${restaurantId}/upload/cover`, {
      method: 'POST',
      headers: {
        ...(initData && { 'X-Telegram-Init-Data': initData }),
      },
      body: formData,
    });
  } catch (error) {
    if (isNetworkError(error)) {
      throw new Error('Sin conexión. Verifica tu internet e intenta de nuevo.');
    }
    throw new Error('Error de conexión. Intenta de nuevo.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || getStatusMessage(response.status));
  }

  return response.json();
}


/**
 * Delete cover image for the restaurant
 */
export async function deleteCoverImage(restaurantId: string): Promise<void> {
  await apiFetch(`/api/admin/${restaurantId}/upload/cover`, {
    method: 'DELETE',
  });
}